import React from 'react';
import { View, Text, useWindowDimensions } from 'react-native';
import theme from '../../../theme';

const stepNumStyle = (muted) => ({
  width: 30,
  height: 30,
  borderRadius: theme.radii.full,
  borderWidth: theme.borderWidth.thin,
  borderColor: muted ? theme.colors.textTertiary : theme.colors.accentPrimary,
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const stepNumTextStyle = (muted) => ({
  fontFamily: theme.typography.fontMono,
  fontSize: theme.typography.size.sm,
  color: muted ? theme.colors.textTertiary : theme.colors.accentPrimary,
});

function Step({ number, title, body, muted }) {
  return (
    <View style={{ flexDirection: 'row', gap: 14 }}>
      <View style={stepNumStyle(muted)}>
        <Text style={stepNumTextStyle(muted)}>{number}</Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ fontFamily: theme.typography.fontSans, fontWeight: theme.typography.weight.semibold, fontSize: theme.typography.size.base, marginBottom: 6 }}>
          {title}
        </Text>
        <Text style={{ fontFamily: theme.typography.fontSans, fontSize: theme.typography.size.sm, color: theme.colors.textSecondary, lineHeight: theme.lineHeightPx(theme.typography.size.sm, 'normal') }}>
          {body}
        </Text>
      </View>
    </View>
  );
}

function PhaseCard({ label, children }) {
  return (
    <View style={{
      borderWidth: theme.borderWidth.thin,
      borderColor: theme.colors.borderDefault,
      borderRadius: theme.radii.lg,
      padding: 28,
      marginBottom: 24,
    }}>
      <Text style={{
        fontFamily: theme.typography.fontMono,
        fontSize: theme.typography.size['2xs'],
        letterSpacing: theme.trackingPx(theme.typography.size['2xs'], 'widest'),
        textTransform: 'uppercase',
        color: theme.colors.textTertiary,
        marginBottom: 18,
      }}>
        {label}
      </Text>
      {children}
    </View>
  );
}

export default function AgentsDocsArticle() {
  const { width } = useWindowDimensions();
  const isCompact = width < 480;

  return (
    <View style={{ paddingVertical: 44, paddingHorizontal: isCompact ? 20 : 48, maxWidth: 780, width: '100%', alignSelf: 'center' }}>
        <Text style={{
        fontFamily: theme.typography.fontDisplay,
        fontWeight: theme.typography.weight.regular,
        fontSize: theme.typography.size['3xl'],
        letterSpacing: theme.trackingPx(theme.typography.size['3xl'], 'tight'),
        marginBottom: 14,
        textAlign: isCompact ? 'center' : 'left',
        }}>
        How it works
        </Text>
        <Text style={{
        fontFamily: theme.typography.fontSans,
        fontSize: theme.typography.size.md,
        color: theme.colors.textSecondary,
        lineHeight: theme.lineHeightPx(theme.typography.size.md, 'normal'),
        marginBottom: 40,
        maxWidth: 560,
        textAlign: isCompact ? 'center' : 'left',
        alignSelf: isCompact ? 'center' : 'flex-start',
        }}>
        Set the agent up once with your tables and vocabulary, then every question that follows reuses that context.
        </Text>

      <PhaseCard label="Phase 1 — Set up">
        <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 24 : 32 }}>
          <View style={{ flex: 1 }}>
            <Step
              number={1}
              title="Define tables"
              body="Point the agent at the BigQuery tables it's allowed to query."
            />
          </View>
          <View style={{ flex: 1 }}>
            <Step
              number={2}
              title="Create a glossary"
              body="Define your team's terms and calculations, so questions map to the right columns."
            />
          </View>
        </View>
      </PhaseCard>

      <PhaseCard label="Phase 2 — Ask">
        <View style={{ gap: 22 }}>
          <Step number={3} title="Ask a question" body="Type it in plain English — a one-off question, or a follow-up in an ongoing thread." />
          <Step number={4} title="Agent thinks" body="It plans an approach, deciding which tables, terms, and tools the question needs." />
          <Step number={5} title="Agent builds a query" body="Translates that plan into a live SQL query against your data." />
          <Step number={6} title="Agent returns an answer" body="Runs the query and drafts a direct response to the original question." />
          <Step number={7} title="Answer visualised" body="Rendered as plain text, a table, or a chart — whichever fits the question best." />
        </View>
      </PhaseCard>

      <View style={{
        backgroundColor: theme.colors.surfaceSunken,
        borderWidth: theme.borderWidth.thin,
        borderColor: theme.colors.borderDefault,
        borderRadius: theme.radii.lg,
        padding: 22,
      }}>
        <Step
          number={8}
          muted
          title="Whole conversation stored server-side"
          body="Every question and answer in the thread is kept, even though only recent turns are sent back to the agent as context — so the model's context window never means lost history for later analysis."
        />
      </View>
    </View>
  );
}